import { useEffect, useState } from 'react';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import { appendText, selectField, textField, vOneOf, vText, type Extracted } from '@/lib/voiceFill';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Checkbox } from '@/components/ui/Checkbox';
import { LoadingState } from '@/components/ui/States';
import { toast } from '@/hooks/useToast';
import { useBranding } from '@/hooks/useBranding';
import { useEncounterMutation, useErMeta, useMlc } from '../hooks';
import { fetchMlcLetter, recordIntimation, saveMlc } from '../api';
import { Section, errorInfo, timeLabel } from '../ui';
import type { MlcRecord, Workspace } from '../types';

const toLocal = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 16) : '');
const fromLocal = (v: string) => (v ? new Date(v).toISOString() : null);
const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));

/**
 * The MLC / accident register (D4, D13) — restricted to er:mlc. The flag is
 * set at arrival/triage by anyone; this register is completed by an
 * authorised user, at any time including after departure, and is never on the
 * treatment path. Missing fields under the active jurisdiction profile (the
 * seeded Tamil Nadu profile by default) are listed, never enforced.
 */
export default function MlcSection({ ws }: { ws: Workspace }) {
  const id = ws.encounter.id;
  const allowed = ws.can.mlc;
  const mlc = useMlc(id, allowed);
  const meta = useErMeta();
  const { branding } = useBranding();
  const [f, setF] = useState<Partial<MlcRecord>>({});
  const [int, setInt] = useState({ intimation_mode: 'phone', intimation_ref: '', police_station: '' });
  const save = useEncounterMutation(id, (input: Partial<MlcRecord>) => saveMlc(id, input));
  const intimate = useEncounterMutation(id, (input: Parameters<typeof recordIntimation>[1]) => recordIntimation(id, input));

  useEffect(() => { if (mlc.data?.mlc) setF(mlc.data.mlc); }, [mlc.data?.mlc]);

  if (!allowed) {
    const v = ws.mlc;
    return (
      <Section title="Medico-legal case">
        <div className="text-[13px]">
          This encounter is flagged as an MLC{v && 'mlc_number' in v && v.mlc_number ? ` (${v.mlc_number})` : ''}.
          The register is restricted to staff with MLC access. Treatment is never held up by it.
        </div>
      </Section>
    );
  }
  if (mlc.isLoading) return <LoadingState label="Loading the MLC register…" />;

  const record = mlc.data?.mlc ?? null;
  const missing = mlc.data?.missing_fields ?? [];
  const set = <K extends keyof MlcRecord>(k: K, v: MlcRecord[K] | null) => setF((x) => ({ ...x, [k]: v }));
  const run = (p: Promise<unknown>, ok: string) => p.then(() => { mlc.refetch(); toast({ title: ok, variant: 'success' }); })
    .catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }));

  // The register's narrative — what happened, where, and the injuries — is
  // exactly what a doctor says out loud while examining. Times stay typed
  // (a misheard time on a legal record is not worth a saved tap), and the
  // "smell of alcohol" attestation stays a deliberate tick.
  const categories = meta.data?.mlc_categories ?? [];
  const voiceFields: VoiceField[] = [
    ...(categories.length ? [selectField('category', `Category — ${categories.map((c) => `${c.code} (${c.label})`).join(', ')}`, categories.map((c) => c.code))] : []),
    textField('incident_place', 'Place of incident'),
    textField('incident_description', 'History of the incident — what happened, as told'),
    textField('injury_description', 'Injuries — nature, site, size, simple or grievous'),
    textField('police_station', 'Police station (jurisdiction)'),
    textField('officer_name', 'Police officer name'),
    textField('remarks', 'Remarks'),
  ];
  const applyVoice = (x: Extracted) => setF((cur) => {
    const next = { ...cur };
    const cat = vOneOf(x, 'category', categories.map((c) => c.code)); if (cat) next.category = cat;
    for (const k of ['incident_place', 'police_station', 'officer_name'] as const) {
      const t = vText(x, k); if (t) next[k] = t;
    }
    for (const k of ['incident_description', 'injury_description', 'remarks'] as const) {
      const t = vText(x, k); if (t) next[k] = appendText(cur[k], t, '\n');
    }
    return next;
  });

  const submit = () => run(save.mutateAsync({
    category: f.category ?? null, incident_at: f.incident_at ?? null, incident_place: f.incident_place ?? null,
    incident_description: f.incident_description ?? null, police_station: f.police_station ?? null,
    police_arrived_at: f.police_arrived_at ?? null, officer_name: f.officer_name ?? null, officer_id: f.officer_id ?? null,
    injury_description: f.injury_description ?? null, alcohol_smell: f.alcohol_smell ?? null, remarks: f.remarks ?? null,
  }), record ? 'MLC register updated' : 'MLC registered');

  const printLetter = async () => {
    try {
      const letter = await fetchMlcLetter(id);
      const w = window.open('', '_blank');
      if (!w) { toast({ title: 'Allow pop-ups to print the letter', variant: 'warn' }); return; }
      w.document.write(`<!doctype html><html><head><title>${esc(letter.title)}</title>
        <style>body{font-family:Georgia,serif;margin:40px;color:black}header{display:flex;gap:16px;align-items:center;border-bottom:2px solid black;padding-bottom:12px;margin-bottom:24px}
        header img{height:56px}h1{margin:0;font-size:22px}.tag{font-size:12px}pre{white-space:pre-wrap;font-family:inherit;font-size:14px;line-height:1.6}
        footer{margin-top:40px;border-top:1px solid gray;font-size:11px;padding-top:8px}</style></head><body>
        <header>${branding.logo_url ? `<img src="${esc(branding.logo_url)}" alt="">` : ''}<div><h1>${esc(branding.hospital_name)}</h1><div class="tag">${esc(branding.tagline || '')}</div></div></header>
        <pre>${esc(letter.text)}</pre>
        ${branding.footer_text ? `<footer>${esc(branding.footer_text)}</footer>` : ''}
        <script>window.onload=function(){window.print()}</script></body></html>`);
      w.document.close();
    } catch (e) { toast({ title: errorInfo(e).message, variant: 'danger' }); }
  };

  return (
    <div className="grid gap-3 lg:grid-cols-[1.4fr_1fr]">
      <Section title={record ? `MLC register — ${record.mlc_number}` : 'MLC register (not yet created)'}>
        <VoiceFormFiller fields={voiceFields} onFields={applyVoice} label="🎙 Dictate MLC details"
          hint="Road traffic accident near Kangeyam bus stand; hit by a two-wheeler; lacerated wound 4 by 2 cm over left forehead, simple; abrasion right forearm" />
        <div className="grid grid-cols-2 gap-2">
          <FormField label="Category" className="col-span-2">
            <NativeSelect value={f.category ?? ''} onChange={(e) => set('category', e.target.value || null)}>
              <option value="">—</option>
              {(meta.data?.mlc_categories ?? []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </NativeSelect>
          </FormField>
          <FormField label="Incident date/time"><Input type="datetime-local" value={toLocal(f.incident_at)} onChange={(e) => set('incident_at', fromLocal(e.target.value))} /></FormField>
          <FormField label="Place of incident"><Input value={f.incident_place ?? ''} onChange={(e) => set('incident_place', e.target.value)} /></FormField>
          <FormField label="History of the incident" className="col-span-2"><Textarea rows={2} value={f.incident_description ?? ''} onChange={(e) => set('incident_description', e.target.value)} /></FormField>
          <FormField label="Injuries (nature, site, size, simple/grievous)" className="col-span-2"><Textarea rows={3} value={f.injury_description ?? ''} onChange={(e) => set('injury_description', e.target.value)} /></FormField>
          <FormField label="Police station (jurisdiction)"><Input value={f.police_station ?? ''} onChange={(e) => set('police_station', e.target.value)} /></FormField>
          <FormField label="Police arrived at"><Input type="datetime-local" value={toLocal(f.police_arrived_at)} onChange={(e) => set('police_arrived_at', fromLocal(e.target.value))} /></FormField>
          <FormField label="Officer name"><Input value={f.officer_name ?? ''} onChange={(e) => set('officer_name', e.target.value)} /></FormField>
          <FormField label="Officer ID / badge"><Input value={f.officer_id ?? ''} onChange={(e) => set('officer_id', e.target.value)} /></FormField>
          <label className="col-span-2 flex items-center gap-2 text-[12px]"><Checkbox checked={f.alcohol_smell === true} onCheckedChange={(v) => set('alcohol_smell', v === true)} /> Smell of alcohol noted</label>
          <FormField label="Remarks" className="col-span-2"><Textarea rows={2} value={f.remarks ?? ''} onChange={(e) => set('remarks', e.target.value)} /></FormField>
        </div>
        <div className="mt-3 flex justify-end gap-2">
          {record && <Button variant="ghost" onClick={printLetter}>🖨 Print intimation letter</Button>}
          <Button loading={save.isPending} onClick={submit}>{record ? 'Save register' : 'Create MLC entry'}</Button>
        </div>
      </Section>

      <div className="space-y-3">
        <Section title="Completeness">
          {missing.length === 0 ? <Badge variant="success">Complete under the active profile</Badge> : (
            <ul className="space-y-1 text-[12px] text-warn-ink">{missing.map((m) => <li key={m.field}>• {m.label}</li>)}</ul>
          )}
          <div className="mt-2 text-[11px] text-ink-muted">Profile: {mlc.data?.profile?.name ?? '—'}. Missing items are reported, never enforced on care.</div>
        </Section>

        <Section title="Police intimation">
          {record?.intimation_sent_at ? (
            <div className="text-[12px]">
              <Badge variant="success">Intimated</Badge> {timeLabel(record.intimation_sent_at)} by {record.intimation_mode} to {record.police_station}
              {record.intimation_ref ? ` (ref ${record.intimation_ref})` : ''} · {record.intimated_by}
            </div>
          ) : !record ? <div className="text-[12px] text-ink-muted">Create the register entry first.</div> : (
            <div className="grid grid-cols-2 gap-2">
              <FormField label="How">
                <NativeSelect value={int.intimation_mode} onChange={(e) => setInt({ ...int, intimation_mode: e.target.value })}>
                  <option value="phone">Phone</option><option value="in_person">In person</option><option value="letter">Letter</option>
                  <option value="email">Email</option><option value="wireless">Police wireless</option><option value="other">Other</option>
                </NativeSelect>
              </FormField>
              <FormField label="Reference / GD no."><Input value={int.intimation_ref} onChange={(e) => setInt({ ...int, intimation_ref: e.target.value })} /></FormField>
              <FormField label="Police station" className="col-span-2"><Input value={int.police_station || record.police_station || ''} onChange={(e) => setInt({ ...int, police_station: e.target.value })} /></FormField>
              <div className="col-span-2 flex justify-end">
                <Button size="sm" loading={intimate.isPending} onClick={() => run(intimate.mutateAsync({
                  intimation_mode: int.intimation_mode, intimation_ref: int.intimation_ref || undefined,
                  police_station: int.police_station || record.police_station || undefined,
                }), 'Intimation recorded')}>Record intimation (now)</Button>
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
